import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Languages, ArrowRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang, SUPPORTED_LANGS } from "@/i18n/LanguageProvider";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Friendly banner shown once on first visit when the visitor's geo/browser
 * language differs from the currently-selected UI language. Uses `/api/geo`
 * to decide, respects a dismiss-once localStorage flag, never appears again
 * after the visitor acts.
 */
const COPY = {
  fr: {
    text: "Nous parlons français aussi.",
    cta: "Continuer en français",
    dismiss: "Rester en anglais",
  },
  pt: {
    text: "Também falamos português.",
    cta: "Continuar em português",
    dismiss: "Ficar em inglês",
  },
};

export default function LanguageDetectedBanner() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [suggested, setSuggested] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // Never re-show once user has acted.
    let acted = false;
    try {
      acted = !!localStorage.getItem("ix.lang.banner");
    } catch (_) { /* ignore */ }
    if (acted) return undefined;

    // Only offer the banner when the visitor is currently on English and
    // hasn't manually opened a /fr or /pt URL.
    if (lang !== "en") return undefined;

    (async () => {
      try {
        const r = await fetch(`${API}/api/geo`);
        if (!r.ok) return;
        const d = await r.json();
        const s = d?.suggested_lang;
        if (cancelled) return;
        if (s && SUPPORTED_LANGS.includes(s) && s !== "en") {
          setSuggested(s);
        }
      } catch (_) { /* ignore */ }
    })();

    return () => { cancelled = true; };
  }, [lang]);

  const dismiss = () => {
    try { localStorage.setItem("ix.lang.banner", "dismissed"); } catch (_) { /* ignore */ }
    setSuggested(null);
  };

  const accept = () => {
    if (!suggested) return;
    try { localStorage.setItem("ix.lang.banner", "accepted"); } catch (_) { /* ignore */ }
    const next =
      `/${suggested}${location.pathname === "/" ? "" : location.pathname}${location.search}${location.hash}`;
    setSuggested(null);
    navigate(next);
  };

  const copy = suggested ? COPY[suggested] : null;

  return (
    <AnimatePresence>
      {suggested && copy && (
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
          className="fixed top-0 inset-x-0 z-[70] bg-forest text-canvas border-b border-canvas/10"
          role="region"
          aria-label="Language suggestion"
          data-testid="lang-banner"
        >
          <div className="container-x py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
            <Languages className="w-4 h-4 text-lime shrink-0" strokeWidth={2} />
            <span className="text-sm md:text-[15px] font-medium">
              {copy.text}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <button
                type="button"
                onClick={accept}
                className="inline-flex items-center gap-1.5 bg-lime text-ink px-3.5 py-1.5 rounded-full text-xs md:text-sm font-semibold hover:bg-lime/90 transition-colors"
                data-testid="lang-banner-accept"
              >
                {copy.cta}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="text-xs md:text-sm text-canvas/70 hover:text-canvas transition-colors underline underline-offset-2"
                data-testid="lang-banner-dismiss"
              >
                {copy.dismiss}
              </button>
              <button
                type="button"
                onClick={dismiss}
                aria-label="Close"
                className="ml-1 h-7 w-7 rounded-full grid place-items-center hover:bg-canvas/10 transition-colors"
                data-testid="lang-banner-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
